<template>
    <div v-if="can">
        <settings-heading
            :title="$t('settings.competences.title')"
            :description="$t('settings.competences.description')"
            :button="$t('settings.add_new', {subject: $t('settings.competences.single')})"
            button-icon="add-circle"
            @button-click="create"
        />

        <div class="mb-48">

            <loading v-if="fetchingCompetences" class="pt-16"></loading>

            <div v-else-if="competences.length" class="pt-16">

                <div class="mb-32 overflow-auto">
                    <div
                        class="p-8 flex items-center odd:bg-primary-5"
                        v-for="competence in competences"
                        :key="competence.id"
                    >
                        <div class="flex-grow text-primary-60 font-semibold truncate mr-32">
                            {{ competence.title[active_locale] }}
                        </div>
                        <button class="mr-16" title="edit competence" @click="edit(competence)">
                            <svg-icon type="edit" class="max-w-16 max-h-16"></svg-icon>
                        </button>
                        <button class="mr-8" title="delete competence" @click="confirmDelete(competence)">
                            <svg-icon type="delete" class="max-w-16 max-h-16"></svg-icon>
                        </button>
                    </div>
                </div>

                <div v-if="competences.length > 5" class="flex justify-end">
                    <simple-button
                        display="secondary"
                        icon="add-circle"
                        look="primary"
                        @click="create"
                    >
                        {{ $t('settings.add_new', {subject: $t('settings.competences.single')}) }}
                    </simple-button>
                </div>

            </div>

        </div>

        <competence-editor v-if="editing" :competence="editing" @close="stopEdit()"></competence-editor>
        <competence-editor v-if="creating" @close="stopCreate()"></competence-editor>

        <popup-modal-confirm
            v-if="deleting"
            :message="confirmDeleteMessage(deleting)"
            :confirmText="$t('general.delete_subject', {subject: $t('settings.competences.single')})"
            :working="performingDeleteRequest"
            @confirm="deleteCompetence(deleting)"
            @close="cancelDelete">
        </popup-modal-confirm>
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import Loading from '../../components/ui/Loading.vue';
import SimpleButton from '../../components/ui/SimpleButton.vue';
import * as CompetenceService from '../../services/CompetenceService'
import Competence from '../../models/Competence';
import { normalizeJson } from '../../../shared/helpers';
import { mapState, mapActions } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useAuthenticationStore} from '../../state/modules/authentication';
import {useNotificationsStore} from '../../state/modules/notifications';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import CompetenceEditor from '../../components/ui/CompetenceEditor.vue';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Error403 from '../../components/ui/Error403.vue';
import SettingsHeading from "../../components/ui/SettingsHeading.vue";


export default {
    components: {
        SettingsHeading,
        'loading': Loading,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'competence-editor': CompetenceEditor,
        'error-403': Error403,
    },
    data: function () {
        return {
            fetchingCompetences: true,
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,

        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'locale']),
        ...mapState(useAuthenticationStore, ['currentUser']),
        competences() {
            return useRepo(Competence).with('tags').orderBy('title').get();
        },
        can() {
            return this.$can('settings.applications');
        },
        active_locale() {
            return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetchCompetences () {
            return CompetenceService.index().then(result => {
                return useRepo(Competence).save(normalizeJson(result.data));
            });
        },
        create () {
            this.creating = true;
        },
        stopCreate () {
            this.creating = false;
        },
        edit (competence) {
            this.editing = competence;
        },
        stopEdit () {
            this.editing = null;
        },
        confirmDelete (competence) {
            this.deleting = competence;
        },
        cancelDelete () {
            this.deleting = null;
        },
        deleteCompetence (competence) {
            this.performingDeleteRequest = true;

            CompetenceService.destroy(competence.id).then(() => {
                useRepo(Competence).destroy(competence.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.deleted', {subject: this.$t('settings.competences.single')}),
                });
            });
        },
        confirmDeleteMessage(competence) {
            return competence.used ?
                this.$t('settings.competences.used_confirm') :
                this.$t('general.confirm_delete_subject', {subject: this.$t('settings.competences.single')});
        }
    },
    mounted () {
        this.fetchingCompetences = true;

        this.fetchCompetences().then(inserts => {
            this.fetchingCompetences = false;
        });
    }
}
</script>
